import { useEffect, useState } from 'react';
import { useIsE2E } from '../../../../../../../hooks/useIsE2E';
import usePersistedKeyValueStore from '../../../../../../../hooks/usePersistedKeyValueStore';
import { APITypes } from '../../../../../../../primary-record-types';
import { getIntl } from '../../../../../../../utils/intl';
import { StandardPrompt } from '../../../../../../v2BaseComponents/Prompt';
import Text from '../../../../../../v2BaseComponents/Text';

const intl = getIntl('multiple-selection-warning-prompt', {
    title: 'Actions disabled',
    message:
        'Bulk actions are disabled because the selected clients have different statuses.  To enable bulk actions, select clients with the same status or use the filter to display clients with a specific status that allows the same bulk actions.',
    close: 'Close',
});

export type UseMultipleSelectionWarningPromptArgs = {
    selectedClients: APITypes.organizationService.ListOrganizationClientRecordsResponse['items'];
    areAllActionsDisabled: boolean;
};

export type UseMultipleSelectionWarningPromptReturnValue = {
    Prompt: React.ReactNode;
};

export const useMultipleSelectionWarningPrompt = ({
    selectedClients,
    areAllActionsDisabled,
}: UseMultipleSelectionWarningPromptArgs): UseMultipleSelectionWarningPromptReturnValue => {
    const isE2E = useIsE2E();
    const persistedStore = usePersistedKeyValueStore<boolean>({
        category: 'client-records-table',
        key: 'has-displayed-multiple-selection-warning',
        defaultValue: isE2E, // don't do this for e2e, it'd be a pain
    });
    const [isOpen, setIsOpen] = useState(false);
    const handleOpenToggle = (isOpen: boolean) => {
        setIsOpen(isOpen);
        if (!isOpen) {
            persistedStore.setValue(true);
        }
    };

    const statusCount = [...new Set(selectedClients.map((client) => client.status))].length;
    const isMultiStatusDisabled = areAllActionsDisabled && statusCount > 1;
    useEffect(() => {
        if (isMultiStatusDisabled && !persistedStore.value) {
            handleOpenToggle(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMultiStatusDisabled, persistedStore.value]);

    const Prompt = (
        <StandardPrompt
            isOpen={isOpen && !persistedStore.value}
            title={intl('title')}
            setIsOpen={handleOpenToggle}
            titleIcon="AlertTriangle"
            actions={[
                {
                    title: intl('close'),
                    onPress: () => handleOpenToggle(false),
                    icon: 'X',
                },
            ]}
        >
            <Text variant="body-1">{intl('message')}</Text>
        </StandardPrompt>
    );

    return { Prompt };
};
